/**
 * PATH: packages/ui/src/kanban/components/card-detail/drawer-sections/useCardTabLayout.ts
 * FILE_ID: UI-KANBAN-CARD-TAB-LAYOUT-HOOK-001
 * PURPOSE: Adapt Card-tab order/hide actions to the board preference runtime.
 * ROLE IN ARCHITECTURE: Compatibility hook over KanbanSettingsPreferenceProvider.
 * MAIN EXPORTS: useCardTabLayout.
 * NON-RESPONSIBILITIES: Does not own HTTP, hydration, cache, or revision state.
 * NOTES FOR FUTURE AI: Direct renders are local-only; board mounts use the provider.
 */
import { useCallback } from 'react'

import { useDbViewPreference } from '@quotech/tenant-shared/preferences/useDbViewPreference'

import { useOptionalKanbanSettingsPreferences } from '../../../preferences/KanbanSettingsPreferenceContext'
import {
  CARD_TAB_LAYOUT_SCOPE,
  type CardTabBlockId,
  type CardTabLayout,
  coerceCardTabLayout,
  DEFAULT_CARD_TAB_LAYOUT,
  moveCardTabBlock,
  resetCardTabLayout,
  setCardTabBlockHidden,
  resolveCardTabLayout,
  visibleCardTabBlocks,
} from './cardTabLayout'

export interface UseCardTabLayoutResult {
  layout: CardTabLayout
  visible: CardTabBlockId[]
  move: (id: CardTabBlockId, direction: 'up' | 'down') => void
  setHidden: (id: CardTabBlockId, hidden: boolean) => void
  reset: () => void
  replace: (next: CardTabLayout) => void
}

/**
 * Return scoped Card-tab mutations backed by the sole board preference runtime.
 *
 * Inputs: optional board id used only for effective visible-block resolution.
 * Output: compatible layout, visible ids, transitions, reset, and replacement.
 * Side effects: provider-backed writes, or local-only writes outside a board.
 * Failure modes: durable failures remain provider state and never drop the draft.
 */
export function useCardTabLayout(boardId?: string): UseCardTabLayoutResult {
  const runtime = useOptionalKanbanSettingsPreferences()
  const fallback = useDbViewPreference({
    scopeKey: CARD_TAB_LAYOUT_SCOPE,
    schemaVersion: 2,
    defaults: DEFAULT_CARD_TAB_LAYOUT,
    coerce: (raw) => coerceCardTabLayout(raw),
    hydrateFromServer: false,
  })
  const preference = runtime?.cardTabLayout ?? fallback
  const layout = preference.value

  const move = useCallback((id: CardTabBlockId, direction: 'up' | 'down') => {
    preference.setValue((previous) => moveCardTabBlock(previous, id, direction))
  }, [preference])

  const setHidden = useCallback((id: CardTabBlockId, hidden: boolean) => {
    preference.setValue((previous) => setCardTabBlockHidden(previous, id, hidden))
  }, [preference])

  const reset = useCallback(() => {
    preference.setValue((previous) => resetCardTabLayout(previous, boardId))
  }, [preference, boardId])

  const replace = useCallback((next: CardTabLayout) => {
    preference.setValue(coerceCardTabLayout(next))
  }, [preference])

  return {
    layout,
    visible: visibleCardTabBlocks(resolveCardTabLayout(layout, boardId)),
    move,
    setHidden,
    reset,
    replace,
  }
}
